import React from "react";
import {
  ComputerDesktopIcon,
  DevicePhoneMobileIcon,
  DeviceTabletIcon,
  QuestionMarkCircleIcon,
} from "@heroicons/react/24/outline";
import { UserSegmentEnum } from "../../../types";
import { useGetDeviceStatistics } from "../../../api/queries";
import { pluralize } from "../../../utils/pluralize";

interface DeviceStatisticsWidgetProps {
  selectedSegments: UserSegmentEnum[];
  startDate?: string;
  endDate?: string;
}

const DeviceStatisticsWidget: React.FC<DeviceStatisticsWidgetProps> = ({
  selectedSegments,
  startDate,
  endDate,
}) => {
  const {
    data: deviceData,
    isLoading,
    error,
  } = useGetDeviceStatistics(selectedSegments, startDate, endDate);

  const getDeviceIcon = (deviceType: string) => {
    const type = deviceType.toLowerCase();

    if (type.includes("mobile") || type.includes("phone")) {
      return <DevicePhoneMobileIcon className="w-8 h-8 text-blue-500" />;
    } else if (type.includes("tablet") || type.includes("ipad")) {
      return <DeviceTabletIcon className="w-8 h-8 text-purple-500" />;
    } else if (type.includes("laptop")) {
      return <ComputerDesktopIcon className="w-8 h-8 text-green-600" />;
    } else if (type.includes("desktop") || type.includes("pc")) {
      return <ComputerDesktopIcon className="w-8 h-8 text-orange-600" />;
    } else {
      return <QuestionMarkCircleIcon className="w-8 h-8 text-gray-400" />;
    }
  };

  const getDeviceLabel = (deviceType: string) => {
    if (!deviceType || deviceType === "unknown") {
      return "Unknown";
    }
    return deviceType;
  };

  const getBrowserIcon = (browserName: string) => {
    const name = browserName.toLowerCase();

    // Using smaller icons for browsers with different colors
    if (name.includes("chrome")) {
      return <div className="w-6 h-6 rounded-full bg-yellow-500"></div>;
    } else if (name.includes("firefox")) {
      return <div className="w-6 h-6 rounded-full bg-orange-500"></div>;
    } else if (name.includes("safari")) {
      return <div className="w-6 h-6 rounded-full bg-blue-400"></div>;
    } else if (name.includes("edge")) {
      return <div className="w-6 h-6 rounded-full bg-blue-600"></div>;
    } else {
      return <div className="w-6 h-6 rounded-full bg-gray-400"></div>;
    }
  };

  return (
    <div className="flex flex-col gap-2 p-5 border-2 border-gray-200 rounded-lg shadow-sm h-full">
      <h3 className="text-center m-0 mb-3 text-gray-700 text-lg underline">
        Device & Browser Statistics
      </h3>

      {isLoading ? (
        <div className="text-center text-gray-400 py-8">Loading...</div>
      ) : error ? (
        <div className="text-center text-red-600 py-8">
          Error loading device statistics
        </div>
      ) : !deviceData?.deviceBrowserStats ||
        deviceData.deviceBrowserStats.length === 0 ? (
        <div className="text-center text-gray-400 py-8">
          No device data available
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          {/* Total Logins */}
          <div className="text-center pb-2 border-b border-gray-200">
            <div className="text-3xl font-bold text-electrum-300">
              {deviceData.totalLogins}
            </div>
            <div className="text-sm text-gray-600 mt-1">Total Logins</div>
          </div>

          {/* Device & Browser Combined View */}
          <div className="flex flex-col gap-4">
            {deviceData.deviceBrowserStats.map((device, deviceIndex) => (
              <div
                key={deviceIndex}
                className="border border-gray-300 rounded-lg overflow-hidden"
              >
                {/* Device Header */}
                <div className="flex items-center justify-between p-3 bg-electrum-100 border-b border-gray-300">
                  <div className="flex items-center gap-3">
                    {getDeviceIcon(device.deviceType)}
                    <div>
                      <div className="font-bold text-gray-800">
                        {getDeviceLabel(device.deviceType)}
                      </div>
                      <div className="text-xs text-gray-600">
                        {pluralize("login", device.totalCount, true)}
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-gray-800">
                      {device.percentage.toFixed(1)}%
                    </div>
                    <div className="text-xs text-gray-600">of total</div>
                  </div>
                </div>

                {/* Browsers for this device */}
                <div className="bg-white">
                  {device.browsers.map((browser, browserIndex) => (
                    <div
                      key={browserIndex}
                      className="flex items-center justify-between p-3 hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0"
                    >
                      <div className="flex items-center gap-3 pl-4">
                        {getBrowserIcon(browser.browserName)}
                        <div>
                          <div className="font-semibold text-gray-700 text-sm">
                            {browser.browserName}
                          </div>
                          <div className="text-xs text-gray-500">
                            {pluralize(
                              "unique user",
                              browser.uniqueUserCount,
                              true,
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-base font-bold text-electrum-300">
                          {browser.count}
                        </div>
                        <div className="text-xs text-gray-500">
                          {browser.percentage.toFixed(1)}% of device
                        </div>
                        <div className="text-xs text-gray-400">
                          {browser.percentageOfTotal.toFixed(1)}% overall
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default DeviceStatisticsWidget;
